// Operations registry for SeaState — vessels, operations, per-op defaults and
// per-client limits. The shapes here mirror the backend registry API so the UI
// can consume either a live DB response or the static v1 fallback below with no
// branching. Pure data + pure functions; no fetch, no DOM.
//
// Ported from the v1 single-file app's VESSELS / OPS / VESSEL_HULL tables and
// the applyPreset heuristic. Real limits come from per-hull motion response
// (RAOs); the heuristic exists to be calibrated, not trusted.

export interface Vessel {
  id: number;
  key: string;
  name: string;
  loa_m: number | null;
  beam_m: number | null;
  roll_period_s: number | null;
  draft_m: number | null;
  /** Economical / service speed (kn); drives the operational range rings. */
  cruise_kn?: number | null;
  /** Spec-sheet maximum speed (kn); on record, not used for planning ranges. */
  max_kn?: number | null;
  dp: boolean;
  day_only: boolean;
  nearshore: boolean;
  gear: string | null;
  operation_ids: number[];
}

export interface Operation {
  id: number;
  kind: string | null;
  label: string;
}

export interface OpDefault {
  label: string;
  wave_m: number;
  wind_kn: number;
  task_hours: number;
  squall_sensitivity: "low" | "med" | "high";
  dp_station: boolean;
}

export type Defaults = Record<string, OpDefault>;

export interface Limit {
  vessel_id: number;
  operation_id: number;
  wave_m: number;
  wind_kn: number;
  task_hours: number;
}

export interface Registry {
  vessels: Vessel[];
  operations: Operation[];
  defaults: Defaults;
  limits: Limit[];
}

// --- Operation kinds --------------------------------------------------------
//
// Operations are numbered 1..9 in the v1 OPS order (coring → transit); a
// vessel's `operation_ids` reference these by id. `kind` is the v1 op key and
// the join key into `defaults`.

const OPERATIONS: readonly Operation[] = [
  { id: 1, kind: "coring", label: "Jumbo piston coring" },
  { id: 2, kind: "cpt", label: "CPT push" },
  { id: 3, kind: "vibracore", label: "Vibracore / box core" },
  { id: 4, kind: "streamer", label: "2DHR streamer seismic" },
  { id: 5, kind: "mbes", label: "MBES survey" },
  { id: 6, kind: "rovCrane", label: "ROV / AUV L&R — crane or davit" },
  { id: 7, kind: "rovLars", label: "ROV / AUV L&R — heave-comp LARS" },
  { id: 8, kind: "buoy", label: "Buoy deployment / recovery" },
  { id: 9, kind: "transit", label: "Ocean transit" },
];

// Industry-typical base limits per operation class, keyed by kind. Scaled by
// the applyPreset heuristic per vessel; overridden by a client `limits` row.
const OP_DEFAULTS: Defaults = {
  coring: {
    label: "Jumbo piston coring",
    wave_m: 2.0,
    wind_kn: 22,
    task_hours: 8,
    squall_sensitivity: "med",
    dp_station: true,
  },
  cpt: {
    label: "CPT push",
    wave_m: 1.5,
    wind_kn: 20,
    task_hours: 6,
    squall_sensitivity: "med",
    dp_station: true,
  },
  vibracore: {
    label: "Vibracore / box core",
    wave_m: 2.0,
    wind_kn: 22,
    task_hours: 4,
    squall_sensitivity: "med",
    dp_station: false,
  },
  streamer: {
    label: "2DHR streamer seismic",
    wave_m: 2.5,
    wind_kn: 25,
    task_hours: 12,
    squall_sensitivity: "med",
    dp_station: false,
  },
  mbes: {
    label: "MBES survey",
    wave_m: 3.0,
    wind_kn: 28,
    task_hours: 12,
    squall_sensitivity: "low",
    dp_station: false,
  },
  rovCrane: {
    label: "ROV / AUV L&R — crane or davit",
    wave_m: 1.5,
    wind_kn: 22,
    task_hours: 4,
    squall_sensitivity: "high",
    dp_station: true,
  },
  rovLars: {
    label: "ROV / AUV L&R — heave-comp LARS",
    wave_m: 2.5,
    wind_kn: 25,
    task_hours: 4,
    squall_sensitivity: "high",
    dp_station: true,
  },
  buoy: {
    label: "Buoy deployment / recovery",
    wave_m: 1.5,
    wind_kn: 20,
    task_hours: 6,
    squall_sensitivity: "high",
    dp_station: false,
  },
  transit: {
    label: "Ocean transit",
    wave_m: 4.0,
    wind_kn: 33,
    task_hours: 24,
    squall_sensitivity: "low",
    dp_station: false,
  },
};

// --- Fleet ------------------------------------------------------------------
//
// Vessels numbered 1..6 in v1 order (brooks → barnacle). LOA / beam / roll from
// the v1 VESSEL_HULL table; dp / day_only / nearshore / gear / operation_ids
// from the v1 VESSELS spec sheets. Draft is not in the v1 data → null. Roll
// periods are estimates (T ≈ 3.27·√B, GM assumed).

const VESSELS: readonly Vessel[] = [
  {
    id: 1,
    key: "brooks",
    name: "R/V Brooks McCall — 48.5 m",
    loa_m: 48.5,
    beam_m: 12.19,
    roll_period_s: 11,
    draft_m: 2.98,
    dp: false,
    day_only: false,
    nearshore: false,
    gear: "40 m CPT-stinger · 20 m jumbo piston core · vibracore · stern & stbd A-frames",
    operation_ids: [2, 1, 3, 8, 9],
  },
  {
    id: 2,
    key: "emma",
    name: "R/V Miss Emma McCall — 47 m",
    loa_m: 46.6,
    beam_m: 9.14,
    roll_period_s: 10,
    draft_m: null,
    dp: false,
    day_only: false,
    nearshore: false,
    gear: "0.5/1 m box corer · 6/9 m piston cores · 300 hp bow thruster (environmental sampling)",
    operation_ids: [1, 3, 8, 9],
  },
  {
    id: 3,
    key: "gyre",
    name: "R/V Gyre — 55 m",
    loa_m: 54.7,
    beam_m: 10.97,
    roll_period_s: 11,
    draft_m: 3.5,
    dp: false,
    day_only: false,
    nearshore: false,
    gear: "EM 302/304 deep MBES (to 8000 m) · 40 m CPT-stinger · 20 m jumbo piston core · 2 A-frames",
    operation_ids: [5, 2, 1, 9],
  },
  {
    id: 4,
    key: "proteus",
    name: "R/V Proteus — 49 m",
    loa_m: 49.4,
    beam_m: 11.58,
    roll_period_s: 11,
    draft_m: 3.39,
    dp: false,
    day_only: false,
    nearshore: false,
    gear: "EM-710 MBES · full geotech kit: box/piston cores, 20 m JPC, T-bar, 40 m CPT-stinger, 10 m gCPT",
    operation_ids: [5, 2, 1, 3, 9],
  },
  {
    id: 5,
    key: "nautilus",
    name: "R/V Nautilus — 75 m · DP2",
    loa_m: 74.5,
    beam_m: 17.8,
    roll_period_s: 14,
    draft_m: 4.7,
    dp: true,
    day_only: false,
    nearshore: false,
    gear: "DP2 · Manta-200 moonpool CPT (40–50 m) · RESON MBES · stern A-frame 22.7 t · ROV/AUV (offshore wind)",
    operation_ids: [2, 5, 1, 6, 7, 8, 9],
  },
  {
    id: 6,
    key: "barnacle",
    name: "Barnacle — 8 m nearshore",
    loa_m: 7.9,
    beam_m: 2.8,
    roll_period_s: 4,
    draft_m: null,
    dp: false,
    day_only: true,
    nearshore: true,
    gear: "Edgetech 4600 MBES/side-scan · 216 sub-bottom · cesium magnetometer · DGPS (daylight nearshore survey)",
    operation_ids: [5],
  },
];

/**
 * The v1 fleet + operations as a static Registry, for when the user isn't
 * logged in or the DB is down. No client limits — every limit falls back to the
 * applyPreset heuristic. Returns fresh copies so callers can mutate safely.
 */
export function fallbackRegistry(): Registry {
  return {
    vessels: VESSELS.map((v) => ({ ...v, operation_ids: [...v.operation_ids] })),
    operations: OPERATIONS.map((o) => ({ ...o })),
    defaults: structuredCloneDefaults(OP_DEFAULTS),
    limits: [],
  };
}

function structuredCloneDefaults(defaults: Defaults): Defaults {
  const out: Defaults = {};
  for (const [key, def] of Object.entries(defaults)) out[key] = { ...def };
  return out;
}

// --- Effective limits -------------------------------------------------------

export interface EffectiveLimit {
  wave_m: number;
  wind_kn: number;
  task_hours: number;
  isDefault: boolean;
  squall: "low" | "med" | "high";
  dpStation: boolean;
}

/**
 * The limit to apply for a vessel×operation. Priority:
 *   1. A client `limits` row for this exact pair → used verbatim (isDefault false).
 *   2. Else the op kind's default, run through the v1 applyPreset heuristic
 *      (hull-length factor, DP station-keeping bonus, nearshore caps) → isDefault true.
 *   3. Else (a bespoke op with no kind and no limit row) → null; the caller must
 *      surface a "needs limits" state rather than guess.
 */
export function effectiveLimit(
  vessel: Vessel,
  op: Operation,
  reg: Registry,
): EffectiveLimit | null {
  const row = reg.limits.find(
    (l) => l.vessel_id === vessel.id && l.operation_id === op.id,
  );
  const def = op.kind != null ? reg.defaults[op.kind] : undefined;

  if (row) {
    return {
      wave_m: row.wave_m,
      wind_kn: row.wind_kn,
      task_hours: row.task_hours,
      isDefault: false,
      squall: def?.squall_sensitivity ?? "med",
      dpStation: def?.dp_station ?? false,
    };
  }

  if (!def) return null;

  // v1 applyPreset heuristic: base limit × hull-length factor, with a DP2 bonus
  // for station-keeping work and nearshore caps for shallow-water boats.
  let f =
    vessel.loa_m != null
      ? Math.min(1.25, Math.max(0.6, Math.sqrt(vessel.loa_m / 50)))
      : 1;
  if (vessel.dp && def.dp_station) f *= 1.15;

  let wave = def.wave_m * f;
  let wind = def.wind_kn * Math.min(1.1, Math.max(0.8, f));
  if (vessel.nearshore) {
    wave = Math.min(wave, 1.0);
    wind = Math.min(wind, 16);
  }

  return {
    wave_m: Math.min(5, Math.max(0.5, Math.round(wave / 0.25) * 0.25)),
    wind_kn: Math.min(40, Math.max(5, Math.round(wind))),
    task_hours: def.task_hours,
    isDefault: true,
    squall: def.squall_sensitivity,
    dpStation: def.dp_station,
  };
}
